import axios from "axios";

import { DOCUMENT_EXPORTS_URL } from "../../constants";
import { accessToken } from "../../modules/auth/accessToken";

/**
 * What an upload is for. The service stores `<domain>/<usage>.xlsx` for a template it
 * fills and `<domain>/logo.<ext>` for the logo the documents carry, and owns the list;
 * it arrives with the template listing as dropdown options saying what each takes, so
 * a new usage is a service-side change only.
 */
export type Usage = string;

/** A workbook the service fills, or an image the documents are built with. */
export type UsageKind = "xlsx" | "image";

export interface UsageOption {
    value: Usage;
    label: string;
    kind: UsageKind;
    /** The file name extensions the service takes for it, e.g. `[".png", ".jpg", ".jpeg", ".svg"]`. */
    accept: string[];
}

/** One brand's template record for a usage, as the service keeps it in meta.json. */
export interface TemplateMeta {
    created?: string;
    updated?: string;
    /** The uploader's email (or Auth0 subject). */
    user?: string;
    /** Required on upload (at least 5 characters): what changed, and why. */
    comment?: string;
    /** The stored file name (`calculations.xlsx`, `logo.svg`): what to ask the download for. */
    file?: string;
}

/** A template upload in an application's change history (`<domain>/history.json` on the service). */
export interface TemplateHistoryEntry {
    timestamp: string;
    user: string;
    /** The usage whose template was uploaded, e.g. `calculations`. */
    template: Usage;
    comment: string;
}

/** A text edited in PIM, one entry per changed language. */
export interface DataHistoryEntry {
    timestamp: string;
    user: string;
    /** The text property that changed, e.g. `disclaimer`. */
    data: string;
    language: string;
    /** The new text; empty when the language was removed. */
    value: string;
}

/** One line of an application's change history, oldest first. */
export type HistoryEntry = TemplateHistoryEntry | DataHistoryEntry;

/** Applications that have templates, keyed by lowercased application domain, then by usage. */
export type TemplateMetas = Record<string, Partial<Record<Usage, TemplateMeta>>>;

export interface UploadResult {
    ok: boolean;
    application: string;
    stored: string;
    errors: string[];
    warnings: string[];
}

const API = `${ DOCUMENT_EXPORTS_URL }/api`;

// The service verifies the console's own platform token and requires the staff
// organisation, so nothing beyond the bearer is needed.
async function authHeaders() {
    const token = await accessToken();
    if (!token) throw new Error("Not signed in");
    return { Authorization: `Bearer ${ token }` };
}

/**
 * A text the service fills into every template of an application, per language
 * (`<domain>/data.json`), edited here and never sent by the apps. The service
 * owns the list; it arrives with the template listing.
 */
export interface DataProperty {
    value: string;
    label: string;
    multiline?: boolean;
}

/** One property's texts by language code, e.g. `{ en: "…", cs: "…" }`. */
export type DataTexts = Record<string, string>;

/** Every property's texts for an application, as data.json keeps them. */
export type ApplicationData = Record<string, DataTexts>;

export interface TemplateListing {
    usages: UsageOption[];
    dataProperties: DataProperty[];
    templates: TemplateMetas;
}

export async function listTemplates(): Promise<TemplateListing> {
    const response = await axios.get<{ ok: boolean } & TemplateListing>(
        `${ API }/templates`,
        { headers: await authHeaders() },
    );
    const { usages, dataProperties, templates } = response.data;
    return { usages, dataProperties: dataProperties ?? [], templates };
}

/** The application's texts, every property with its languages. */
export async function fetchData(application: string): Promise<ApplicationData> {
    const response = await axios.get<{ ok: boolean, application: string, data: ApplicationData }>(
        `${ API }/templates/${ encodeURIComponent(application) }/data`,
        { headers: await authHeaders() },
    );
    return response.data.data;
}

export interface SaveDataResult {
    ok: boolean;
    application: string;
    property: string;
    /** The property's texts as stored after the save. */
    data?: DataTexts;
    /** The languages whose text changed — each is one history entry. */
    changed?: string[];
    errors?: string[];
}

/**
 * Replace one property's texts, all languages in one atomic save. A blank text removes
 * the language. 400/404 answers resolve with the JSON shape; auth failures throw.
 */
export async function saveData(application: string, property: string, texts: DataTexts): Promise<SaveDataResult> {
    const response = await axios.put<SaveDataResult>(
        `${ API }/templates/${ encodeURIComponent(application) }/data/${ encodeURIComponent(property) }`,
        texts,
        {
            headers: await authHeaders(),
            validateStatus: (status) => status < 500 && status !== 401 && status !== 403,
        },
    );
    return response.data;
}

/**
 * Upload (or replace) an application's template for a usage. The service validates
 * the workbook first; 409 (exists, no overwrite) and 422 (failed validation) carry
 * the same JSON shape as success, so they resolve rather than throw.
 */
export async function uploadTemplate(
    input: { application: string, usage: Usage, file: File, comment: string, overwrite: boolean },
    onProgress?: (uploaded: number, total: number) => void,
): Promise<UploadResult> {
    const form = new FormData();
    form.set("application", input.application);
    form.set("usage", input.usage);
    form.set("comment", input.comment);
    if (input.overwrite) form.set("overwrite", "true");
    form.set("template", input.file, input.file.name);

    const response = await axios.post<UploadResult>(
        `${ API }/templates`,
        form,
        {
            headers: await authHeaders(),
            onUploadProgress(event) {
                onProgress?.(event.loaded, event.total ?? input.file.size);
            },
            // 4xx outcomes are answers (JSON body); auth failures and 5xx are errors.
            validateStatus: (status) => status < 500 && status !== 401 && status !== 403,
        },
    );
    return response.data;
}

/**
 * A stored file as a Blob — `file` is the listing's `meta.file`. The endpoint needs the
 * bearer, so a plain link or `<img src>` cannot do.
 */
export async function downloadTemplate(application: string, file: string): Promise<Blob> {
    const response = await axios.get<Blob>(
        `${ API }/templates/${ encodeURIComponent(application) }/${ encodeURIComponent(file) }`,
        { headers: await authHeaders(), responseType: "blob" },
    );
    return response.data;
}

/** Every upload for the application, oldest first — who changed which template, when, and why. */
export async function fetchHistory(application: string): Promise<HistoryEntry[]> {
    const response = await axios.get<{ ok: boolean, application: string, history: HistoryEntry[] }>(
        `${ API }/templates/${ encodeURIComponent(application) }/history`,
        { headers: await authHeaders() },
    );
    return response.data.history;
}
